import axios from 'axios';
import { API_URL } from '../utils/constant';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('session');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const fetchThreatActors = async (params) => {
    try {
        const { 
            page, 
            limit, 
            search, 
            sortBy, 
            sortOrder, 
            filters 
        } = params;

        // Construct query parameters
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sortOrder: sortOrder,
            sortBy: `misp_data.${sortBy}` // Prefix with misp_data to match API structure
        });

        // Add search if present
        if (search) {
            queryParams.append('search', search);
        }

        // Add filter parameters if they exist
        if (filters) {
            if (filters.attribution?.length) {
                queryParams.append('attribution', filters.attribution.join(','));
            }
            if (filters.activityType?.length) {
                queryParams.append('activityType', filters.activityType.join(','));
            }
            if (filters.targetCountries?.length) {
                queryParams.append('targetCountries', filters.targetCountries.join(','));
            }
            if (filters.targetSectors?.length) {
                queryParams.append('targetSectors', filters.targetSectors.join(','));
            }
            if (filters.dateRange) {
                queryParams.append('dateFrom', filters.dateRange[0].toISOString());
                queryParams.append('dateTo', filters.dateRange[1].toISOString());
            }
        }

        const response = await api.get(`/api/threat-intel/threat-actors?${queryParams}`);

        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to fetch threat actors'
        );
    }
};

export const fetchThreatActorById = async (id) => {
    try {
        const response = await api.get(`/api/threat-intel/threat-actors/${id}`);
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to fetch threat actor details'
        );
    }
};

export const fetchThreatActorStatistics = async () => {
    try {
        const response = await api.get('/api/threat-intel/threat-actors/statistics');
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to fetch threat actor statistics'
        );
    }
};

export const fetchFilterOptions = async () => {
    try {
        const response = await api.get('/api/threat-intel/threat-actors/filter-options');
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to fetch filter options'
        );
    }
};

export const exportThreatActors = async (format = 'csv', filters = {}) => {
    try {
        const queryParams = new URLSearchParams({
            format,
            ...filters
        });

        const response = await api.get(`/api/threat-intel/threat-actors/export?${queryParams}`, {
            responseType: 'blob'
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `threat-actors.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        return true;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to export threat actors'
        );
    }
};

// Industry-Country API endpoints
export const fetchThreatActorsByIndustryCountry = async (industry, country, params = {}) => {
    try {
        const { count = 50, cursor = null, orderBy = 'name', orderMode = 'desc' } = params;
        
        const queryParams = new URLSearchParams({
            industry,
            country,
            count: count.toString(),
            orderBy,
            orderMode
        });

        if (cursor) {
            queryParams.append('cursor', cursor);
        }

        const response = await api.get(`/threat-intel/threat-actors/industry-country?${queryParams}`);
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.userMessage || 
            'Failed to fetch threat actors by industry and country'
        );
    }
};

export const fetchMalwareByIndustryCountry = async (industry, country, params = {}) => {
    try {
        const { count = 50, cursor = null, orderBy = 'name', orderMode = 'desc' } = params;
        
        const queryParams = new URLSearchParams({
            industry,
            country,
            count: count.toString(),
            orderBy,
            orderMode
        });

        if (cursor) {
            queryParams.append('cursor', cursor);
        }

        const response = await api.get(`/threat-intel/malware/industry-country?${queryParams}`);
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.userMessage || 
            'Failed to fetch malware by industry and country'
        );
    }
};

export const fetchTTPsByIndustryCountry = async (industry, country, params = {}) => {
    try {
        const { count = 50, cursor = null, orderBy = 'name', orderMode = 'desc' } = params;
        
        const queryParams = new URLSearchParams({
            industry,
            country,
            count: count.toString(),
            orderBy,
            orderMode
        });

        if (cursor) {
            queryParams.append('cursor', cursor);
        }

        const response = await api.get(`/threat-intel/ttps/industry-country?${queryParams}`);
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.userMessage || 
            'Failed to fetch TTPs by industry and country'
        );
    }
};

export const fetchThreatIntelFeeds = async (page = 1, limit = 10) => {
    try {
        const response = await api.get(`/threat-intel-feeds?page=${page}&limit=${limit}`);
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 
            'Failed to fetch threat intelligence feeds'
        );
    }
};

export const fetchSectorsAndCountries = async () => {
    try {
        const response = await api.get('/threat-intel/sectors-countries');
        return response.data;
    } catch (error) {
        throw new Error(
            error.response?.data?.userMessage || 
            'Failed to fetch sectors and countries'
        );
    }
}; 